import axiosClient from './axiosClient';
import { IBaseResponse, IListDataResponse } from '~/utils/interface/base';
import { phoneFromForm, phoneToForm, toMultipart } from '~/utils/helper/setupForm';
import { FormValues, ICollectionAdapter, IPhone } from '~/utils/interface/setup';

const urlApiContact = 'contacts';

export interface IContactRow {
  id: string;
  username: string;
  email: string;
  firstName?: string | null;
  lastName?: string | null;
  phone?: IPhone | null;
  isEmergency?: boolean;
  isFallDetection?: boolean;
  alertText?: boolean | null;
  alertSound?: boolean | null;
  alertVibration?: boolean | null;
  languageCode?: string | null;
  languageVoiceCode?: string | null;
  ringtone?: string | null;
  avatarImage?: string | null;
}

// POST's schema rejects '' for phone and names (PUT's allows it), so blanks are left out on create.
const blank = (value: FormValues[string], omit: boolean) => {
  const text = String(value ?? '').trim();
  return omit && !text ? undefined : text;
};

// SOS / Alerts membership isn't part of the contact's own PUT/POST: the app sets it with
// PUT /contacts/:id/type, sending both flags (+ alert options for the Alerts list).
const setListMembership = (accountId: string | number, form: FormValues) =>
  axiosClient.put(`${urlApiContact}/${accountId}/type`, {
    isEmergency: form.isEmergency === true,
    isFallDetection: form.isFallDetection === true,
    ...(form.isFallDetection === true
      ? {
          alertText: form.alertText !== false,
          alertSound: form.alertSound !== false,
          alertVibration: form.alertVibration === true,
        }
      : {}),
  });

const toBody = (form: FormValues, isCreate: boolean) => {
  const phone = phoneFromForm(form);
  const hasPhone = !!(phone.countryCode || phone.phoneNumber);

  return toMultipart({
    username: String(form.username ?? '').trim(),
    email: String(form.email ?? '').trim(),
    firstName: blank(form.firstName, isCreate),
    lastName: blank(form.lastName, isCreate),
    phone: isCreate && !hasPhone ? undefined : phone,
    languageCode: String(form.languageCode ?? ''),
    languageVoiceCode: String(form.languageVoiceCode ?? ''),
    ringtone: String(form.ringtone ?? ''),
    // a string here is the already-saved url; only a newly picked File is sent
    avatarImage: form.avatarImage instanceof File ? form.avatarImage : undefined,
  });
};

export const contactAdapter: ICollectionAdapter<IContactRow> = {
  list: async ({ page, limit, textSearch }) => {
    // first name, then last name, A-Z: without a sort the backend falls back to the family's app setting,
    // or to no ORDER BY at all (unstable pages). Not saved as that setting: only updateSortContact does.
    const resp = await axiosClient.get<IBaseResponse<IListDataResponse<IContactRow[]>>>(urlApiContact, {
      params: { isContact: true, page, limit, textSearch: textSearch || undefined, sortBy: 'FIRST_NAME', sortType: 'ASC' },
    });
    return { rows: resp.data.data.responses, totalItems: resp.data.data.pagination.totalItems };
  },
  getId: (row) => String(row.id),
  rowToForm: (row) => ({
    username: row.username ?? '',
    email: row.email ?? '',
    firstName: row.firstName ?? '',
    lastName: row.lastName ?? '',
    ...phoneToForm(row.phone),
    isEmergency: row.isEmergency ?? false,
    isFallDetection: row.isFallDetection ?? false,
    // app defaults: text + sound on, vibration off
    alertText: row.alertText ?? true,
    alertSound: row.alertSound ?? true,
    alertVibration: row.alertVibration ?? false,
    languageCode: row.languageCode ?? 'en-US',
    languageVoiceCode: row.languageVoiceCode ?? 'en-US',
    ringtone: row.ringtone ?? '',
    avatarImage: row.avatarImage ?? '',
  }),
  create: async (form) => {
    const resp = await axiosClient.post<IBaseResponse<{ accountId?: string | number }>>(
      urlApiContact,
      toBody(form, true),
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    if (form.isEmergency !== true && form.isFallDetection !== true) return resp;

    const accountId = resp.data.data?.accountId;
    if (!accountId) throw new Error('Contact created, but its id was not returned to set the SOS / Alerts lists');
    return setListMembership(accountId, form);
  },
  update: async (id, form) => {
    await axiosClient.put(`${urlApiContact}/${id}`, toBody(form, false), {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return setListMembership(id, form);
  },
  remove: (id) => axiosClient.delete(`${urlApiContact}/${id}`),
};
